import { saveAs } from 'file-saver';
import React from 'react';
import { toast } from 'react-toastify';

import { HttpCodes } from '../enums/httpCodes';
import { HttpService } from '../services/http.service';

export const useDownload = () => {
  const [isDownloading, setIsDownloading] = React.useState<boolean>(false);

  const [timeoutId, setTimeoutId] = React.useState<null | ReturnType<typeof setTimeout>>(null);

  const abortFunc = React.useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setIsDownloading(false);
  }, [timeoutId]);

  const saveFileDirectly = async (url: string, name: string) => {
    if (!url) {
      return;
    }

    setIsDownloading(true);
    await HttpService.fetchAuthorized(url, {
      method: 'GET',
    })
      .then((res) => {
        const file = new File([res], name);
        saveAs(file);

        toast.dismiss(name);
        toast.success(`File ${name} has been downloaded`, {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const checkIfFileExistsAndDownload = async (url: string, name: string, withoutAuth = false, timeout = 3000) => {
    setIsDownloading(true);
    const res = await HttpService.downloadFile(url, withoutAuth);

    if (res.status === HttpCodes.NOT_FOUND || res.status === HttpCodes.ACCEPTED) {
      const idFromTimeout = setTimeout(() => checkIfFileExistsAndDownload(url, name, withoutAuth, timeout), timeout);

      toast.info(`File ${name} is being created`, {
        position: 'top-center',
        autoClose: false,
        toastId: name,
      });

      setTimeoutId(idFromTimeout);
    } else if (res.status === HttpCodes.SERVER_ERROR || res.status === HttpCodes.TIMEOUT) {
      setIsDownloading(false);
      toast.dismiss(name);
      toast.error(`There was an error creating file ${name}.`);
      setTimeoutId(null);
    } else {
      res.blob().then((blob) => {
        saveAs(blob, name);
        setIsDownloading(false);
        setTimeoutId(null);

        toast.dismiss(name);
        toast.success(`File ${name} has been downloaded`, {
          position: 'top-center',
          autoClose: 4000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
        });
      });
    }
  };

  return { isDownloading, setIsDownloading, checkIfFileExistsAndDownload, abortFunc, saveFileDirectly };
};
